import { lazy } from 'react';
import type { NavIconName } from '../components/NavIcons';

// Lazy-loaded section components
const ESGPage = lazy(() => import('../components/ESGPage'));
const CustomerQuestions = lazy(() => import('../components/CustomerQuestions'));
const SustainabilityCredentials = lazy(() => import('../components/SustainabilityCredentials'));
const SupplierEngagement = lazy(() => import('../components/SupplierEngagement'));
const Products = lazy(() => import('../components/Products'));
const Purchases = lazy(() => import('../components/Purchases'));
const Sales = lazy(() => import('../components/Sales'));
const CustomerList = lazy(() => import('../components/customers/CustomerList'));
const OutboundShipmentImportPage = lazy(
  () => import('../components/import/pages/outboundShipments'),
);
const GHGInventory = lazy(() => import('../components/GHGInventory'));
const Facilities = lazy(() => import('../components/Facilities'));
const Shipments = lazy(() => import('../components/Shipments'));
const FreightImportPage = lazy(() => import('../components/freight/FreightImportPage'));
const Travel = lazy(() => import('../components/Travel'));
const Investments = lazy(() => import('../components/Investments'));
const Frameworks = lazy(() => import('../components/Frameworks'));
const FrameworkDetail = lazy(() => import('../components/FrameworkDetail'));
const Strategy = lazy(() => import('../components/strategy/Strategy'));
const NewsAlerts = lazy(() => import('../components/NewsAlerts'));
const Audit = lazy(() => import('../components/Audit'));
const Settings = lazy(() => import('../components/Settings'));
const Measurement = lazy(() => import('../components/Measurement'));
const RecipeImportPage = lazy(() => import('../components/import/pages/recipes'));
// Reporting v2 lives in both SECTION_ROUTES (the navbar entry below)
// and SUB_ROUTES (its child paths). Declared at module top so the
// SECTION_ROUTES initialiser can reference it without a temporal-dead-
// zone error.
// ``/reporting`` landing: redirects a bare entry to the first dashboard,
// falling back to the Widget Builder (which it lazy-loads itself) when the
// org has no dashboards or the builder is explicitly requested.
const ReportingV2Home = lazy(
  () => import('../components/reporting-v2/ReportingV2Home'),
);
const DashboardViewV2 = lazy(
  () => import('../components/reporting-v2/DashboardView'),
);

/** Top-level navbar section. Items are grouped under these in render order. */
export type NavSectionKey =
  | 'profile'
  | 'value_chain'
  | 'emissions'
  | 'insights'
  | 'settings';

export const NAV_SECTIONS: { key: NavSectionKey; label: string }[] = [
  { key: 'insights',    label: 'Insights & compliance' },
  { key: 'profile',     label: 'Your sustainability profile' },
  { key: 'emissions',   label: 'Emissions' },
  { key: 'value_chain', label: 'Value chain' },
  { key: 'settings',    label: 'Settings' },
];

export interface RouteConfig {
  path: string;
  label: string;
  featureKey: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  /** Which sidebar section this item lives under. */
  section: NavSectionKey;
  /** Short benefit-led tagline shown on hover (Variant A) or inline (Variant B). */
  tagline: string;
  /** Lucide-style icon identifier from NavIcons. */
  icon: NavIconName;
  /** GHG inventory category codes this page covers. The page hides from
   *  nav when EVERY listed category has been marked Not Relevant or
   *  Excluded on the GHG Inventory page. Omit for pages whose visibility
   *  is unrelated to GHG inventory status (Purchases, Sales, etc.). */
  inventoryCategories?: string[];
  /** Internal-staff-only — gate the nav item (and ProtectedRoute
   *  guard) behind ``userPermissions.is_system_admin``. Use during
   *  staged rollout: the route exists for support staff to test the
   *  in-flight feature without exposing it to paying customers. The
   *  ProtectedRoute already bypasses ``hasFeature`` for sys admins,
   *  so an unentitled featureKey is enough on its own — this flag
   *  just keeps the item out of every other user's navbar. */
  internalOnly?: boolean;
}

/**
 * All navigable items, grouped by section. Order within each section
 * is preserved in the rendered navbar.
 */
export const SECTION_ROUTES: RouteConfig[] = [
  // Your sustainability profile
  { path: '/esg',                    label: 'ESG self-assessment',          featureKey: 'esg_assessment',         component: ESGPage,                  section: 'profile',     tagline: 'See where you stand — and pre-answer most customer questions', icon: 'clipboard' },
  { path: '/customer-questions',     label: 'Customer questions',           featureKey: 'customer_questions',     component: CustomerQuestions,        section: 'profile',     tagline: 'Answer any customer questionnaire in minutes',                icon: 'message' },
  { path: '/credentials',            label: 'Your sustainability credentials', featureKey: 'credentials',         component: SustainabilityCredentials, section: 'profile',     tagline: 'Share your evidence, goals and progress in one link',             icon: 'award' },

  // Value chain
  { path: '/supplier-engagement',    label: 'Suppliers',                    featureKey: 'supplier_engagement',    component: SupplierEngagement,       section: 'value_chain', tagline: 'Collect supplier data without chasing emails',                icon: 'truck' },
  { path: '/products',               label: 'Products',                     featureKey: 'products',               component: Products,                 section: 'value_chain', tagline: 'From supplier data to ready-made product specs',              icon: 'package' },
  { path: '/purchases',              label: 'Purchases',                    featureKey: 'purchases',              component: Purchases,                section: 'value_chain', tagline: 'Import once — suppliers and products, populated automatically', icon: 'cart' },
  { path: '/sales',                  label: 'Sales',                        featureKey: 'sales',                  component: Sales,                    section: 'value_chain', tagline: 'Import once — customers and orders, populated automatically',  icon: 'receipt' },
  { path: '/customers',              label: 'Customers',                    featureKey: 'customers',              component: CustomerList,             section: 'value_chain', tagline: 'Track every customer you sell to and report by market',         icon: 'users' },

  // Emissions
  { path: '/emissions',              label: 'GHG inventory',                featureKey: 'ghg_inventory',          component: GHGInventory,             section: 'emissions',   tagline: 'Every gram of CO₂e, traced to source',                    icon: 'cloud' },
  { path: '/facilities',             label: 'Facilities & fleet',           featureKey: 'sites',                  component: Facilities,               section: 'emissions',   tagline: 'Utilities, fuel, waste and refrigerants — site by site, vehicle by vehicle', icon: 'buildings' },
  { path: '/shipments',              label: 'Shipments',                    featureKey: 'shipments',              component: Shipments,                section: 'emissions',   tagline: 'Spot reduction opportunities in every shipment',               icon: 'ship',         inventoryCategories: ['scope_3_4', 'scope_3_9'] },
  { path: '/travel',                 label: 'Travel',                       featureKey: 'travel',                 component: Travel,                   section: 'emissions',   tagline: 'Capture business travel and employee commuting',               icon: 'plane' },
  { path: '/investments',            label: 'Investments',                  featureKey: 'investments',            component: Investments,              section: 'emissions',   tagline: 'Allocate emissions to your investment portfolio',              icon: 'trending-up',  inventoryCategories: ['scope_3_15'] },

  // Insights & compliance
  // Reporting (registry-driven "v2") is now THE reporting experience for
  // every entitled org: it owns the canonical ``/reporting`` path + the
  // real ``reporting`` feature key. ``ReportingV2Home`` lands a bare visit
  // on the org's first dashboard (builder fallback when they have none).
  { path: '/reporting',              label: 'Dashboards',                   featureKey: 'reporting',              component: ReportingV2Home,          section: 'insights',    tagline: 'See your sustainability story at a glance',                   icon: 'chart' },
  // Reporting v1 ("Dashboards (legacy)") was retired — its UI is archived under
  // frontend/legacy-reporting-v1/. /reporting/v1 now redirects to /reporting
  // (see App.tsx).
  { path: '/frameworks',             label: 'Frameworks',                   featureKey: 'frameworks',             component: Frameworks,               section: 'insights',    tagline: 'Auto-fill SBTi, CSRD, CDP and every other report',            icon: 'book' },
  { path: '/strategy',               label: 'Strategy',                     featureKey: 'strategy',               component: Strategy,                 section: 'insights',    tagline: 'Set targets, plan actions, prove progress',                   icon: 'target' },
  { path: '/news',                   label: 'News & alerts',                featureKey: 'news_alerts',            component: NewsAlerts,               section: 'insights',    tagline: 'Stay ahead of regulation, recalls and supply shocks',         icon: 'bell' },
  { path: '/audit',                  label: 'Audit trail',                  featureKey: 'audit',                  component: Audit,                    section: 'insights',    tagline: 'Auditor-ready evidence for every number',                     icon: 'search' },

  // Settings (sub-pages promoted to first-class items under the section header)
  { path: '/settings',               label: 'Organisation profile',         featureKey: 'settings',               component: Settings,                 section: 'settings',    tagline: 'Company information and sites',                                icon: 'briefcase' },
  { path: '/settings/users',         label: 'User management',              featureKey: 'settings',               component: lazy(() => import('../components/UserManagement')), section: 'settings', tagline: 'Invite teammates and set their access',                       icon: 'users' },
  { path: '/settings/measurement',   label: 'Metrics & scoring',            featureKey: 'settings',               component: Measurement,              section: 'settings',    tagline: 'Configure measurement and scoring',                            icon: 'sliders' },
];

/** Sub-routes that need their own Route entry but don't appear in the navbar. */
export interface SubRouteConfig {
  path: string;
  featureKey: string;
  component: React.LazyExoticComponent<React.ComponentType<any>>;
}

const RefrigerantImportPage = lazy(
  () => import('../components/import/pages/refrigerants'),
);
const TravelImportPage = lazy(() => import('../components/travel/TravelImportPage'));

export const SUB_ROUTES: SubRouteConfig[] = [
  { path: '/frameworks/:selectionId', featureKey: 'frameworks', component: FrameworkDetail },
  { path: '/imports/refrigerants',    featureKey: 'refrigerants', component: RefrigerantImportPage },
  { path: '/imports/sales',           featureKey: 'sales',        component: OutboundShipmentImportPage },
  { path: '/imports/recipes',         featureKey: 'product_transformations', component: RecipeImportPage },
  { path: '/imports/travel',          featureKey: 'travel',       component: TravelImportPage },
  { path: '/imports/freight',         featureKey: 'freight',      component: FreightImportPage },
  // Reporting dashboard view child paths. The /reporting root path lives
  // in SECTION_ROUTES so it appears in the navbar; these children share
  // its real ``reporting`` feature key so the ProtectedRoute gate matches.
  { path: '/reporting/dashboard',  featureKey: 'reporting', component: DashboardViewV2 },
  { path: '/reporting/dashboard/:dashboardId', featureKey: 'reporting', component: DashboardViewV2 },
];

export const DEFAULT_PATH = '/reporting';
export const LOGIN_PATH = '/login';
export const FORGOT_PASSWORD_PATH = '/forgot-password';
export const RESET_PASSWORD_PATH = '/reset-password';
export const ACCOUNT_PATH = '/account';

/** Find URL path for a section display label (backward compat for custom events). */
export function pathForLabel(label: string): string {
  const route = SECTION_ROUTES.find(r => r.label === label);
  return route?.path ?? DEFAULT_PATH;
}
